import type { AddressForm } from "../api";

/** I dieci campi dell'indirizzo cliente, nello stesso ordine ovunque. */
export const ADDRESS_FIELDS: Array<{ key: keyof AddressForm; label: string; span?: boolean }> = [
  { key: "company", label: "Azienda", span: true },
  { key: "address", label: "Indirizzo", span: true },
  { key: "city", label: "Città" },
  { key: "postcode", label: "CAP" },
  { key: "state_iso", label: "Prov." },
  { key: "country_iso", label: "Paese" },
  { key: "name", label: "Nome" },
  { key: "surname", label: "Cognome" },
  { key: "phone", label: "Telefono" },
  { key: "email", label: "Email" },
];

/** Indirizzo vuoto: è ciò che serve quando si sceglie «Altro indirizzo». */
export const INDIRIZZO_VUOTO: AddressForm = {
  company: "",
  address: "",
  city: "",
  postcode: "",
  state_iso: "",
  country_iso: "",
  name: "",
  surname: "",
  phone: "",
  email: "",
};

export interface CustomerAddressFieldsProps {
  /** Nome accessibile del gruppo (es. «Destinatario (cliente)»). */
  label: string;
  address: AddressForm;
  onEdit: (field: keyof AddressForm, value: string) => void;
  disabled?: boolean;
  /** Chip opzionali sopra i campi: presenti solo dove si può scegliere un
   *  indirizzo diverso da quello dell'ordine. */
  chips?: {
    altro: boolean;
    onStesso: () => void;
    onAltro: () => void;
  };
}

/** Blocco indirizzo del cliente, come `<fieldset>`: la legend dà al gruppo un
 *  nome accessibile, così «Indirizzo» del destinatario non si confonde con
 *  quello del mittente (due blocchi, stesse etichette). */
export function CustomerAddressFields({
  label,
  address,
  onEdit,
  disabled = false,
  chips,
}: CustomerAddressFieldsProps) {
  return (
    <fieldset className="min-w-0" disabled={disabled}>
      <legend className="mb-1.5 text-[11.5px] font-medium text-ink-2">{label}</legend>
      {chips && (
        <div className="mb-2.5 flex flex-wrap gap-1.5">
          {(
            [
              ["Stesso indirizzo dell'ordine", !chips.altro, chips.onStesso],
              ["Altro indirizzo", chips.altro, chips.onAltro],
            ] as const
          ).map(([testo, attivo, onClick]) => (
            <button
              key={testo}
              type="button"
              onClick={onClick}
              aria-pressed={attivo}
              className={`rounded-sm border px-[10px] py-[4px] text-[11.5px] font-medium transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-accent/40 ${
                attivo
                  ? "border-accent bg-accent-soft text-ink"
                  : "border-line bg-card text-ink-2 hover:bg-paper"
              }`}
            >
              {testo}
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-x-3 gap-y-2">
        {ADDRESS_FIELDS.map(({ key, label: fieldLabel, span }) => (
          <label
            key={key}
            className={`flex flex-col gap-0.5 text-[11px] text-ink-2 ${span ? "col-span-2" : ""}`}
          >
            {fieldLabel}
            <input
              type="text"
              value={address[key]}
              onChange={(e) => onEdit(key, e.target.value)}
              className="rounded-sm border border-line px-2 py-1 text-[12px] text-ink focus:border-line-2 focus:outline-none focus-visible:ring-1 focus-visible:ring-accent/40 disabled:opacity-50"
            />
          </label>
        ))}
      </div>
    </fieldset>
  );
}
